import {
  ComposerAttachmentCard,
  ComposerAttachmentFallback,
  ComposerAttachmentInfo,
  ComposerAttachmentKindLabel,
  ComposerAttachmentName,
  ComposerAttachmentPreview,
  ComposerAttachmentRemoveButton,
  ComposerAttachmentsList,
  ComposerAttachmentUploadOverlay
} from "./styles";
import { UploadProgressSpinner } from "./upload-progress-spinner";

export interface AllowedComposerFileType {
  fileType: string;
  mimeTypes: string[];
  sizeLimit: number;
}

export type ComposerAttachmentKind = "image" | "audio" | "video" | "other";

export interface ComposerAttachment {
  id: string;
  file: File;
  previewUrl: string;
  kind: ComposerAttachmentKind;
}

export interface ComposerFilesValidationResult {
  acceptedFiles: File[];
  errorMessage: string | null;
  partialErrorMessage: string | null;
}

export interface AttachmentUploadProgress {
  index: number;
  progress: number;
}

const bytesInMegabyte = 1024 * 1024;

const parseSizeLimitFromEnv = (value?: string) => {
  if (!value?.trim()) return null;

  const parsedValue = Number(value);

  if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
    return null;
  }

  return parsedValue * bytesInMegabyte;
};

const mediaSizeLimitFromEnv = parseSizeLimitFromEnv(
  process.env.OMNICHANNEL_MAX_MEDIA_UPLOAD_SIZE_MB
);

const documentSizeLimitFromEnv = parseSizeLimitFromEnv(
  process.env.OMNICHANNEL_MAX_DOCUMENT_UPLOAD_SIZE_MB
);

export const allowedComposerFileTypes: AllowedComposerFileType[] = [
  {
    fileType: "audio",
    mimeTypes: [
      "audio/aac",
      "audio/mp4",
      "audio/mpeg",
      "audio/amr",
      "audio/ogg opus codecs",
      "audio/ogg",
      "audio/opus"
    ],
    sizeLimit: mediaSizeLimitFromEnv ?? 16 * bytesInMegabyte
  },
  {
    fileType: "image",
    mimeTypes: ["image/jpeg", "image/png", "image/gif"],
    sizeLimit: mediaSizeLimitFromEnv ?? 5 * bytesInMegabyte
  },
  {
    fileType: "sticker",
    mimeTypes: ["image/webp"],
    sizeLimit: 0.5 * bytesInMegabyte
  },
  {
    fileType: "video",
    mimeTypes: ["video/mp4", "video/3gp"],
    sizeLimit: mediaSizeLimitFromEnv ?? 16 * bytesInMegabyte
  },
  {
    fileType: "documents",
    mimeTypes: [
      "text/plain",
      "text/csv",
      "application/pdf",
      "application/json",
      "application/pkcs12",
      "application/x-pkcs12",
      "application/x-pkcs-12",
      "application/msword",
      "application/vnd.ms-excel",
      "application/vnd.ms-powerpoint",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.oasis.opendocument.spreadsheet",
      "application/zip",
      "application/x-zip-compressed",
      "application/vnd.rar",
      "application/x-rar-compressed",
      "image/vnd.dwg",
      "application/coreldraw",
      "application/postscript",
      "application/illustrator",
      "application/x-photoshop",
      "image/svg+xml"
    ],
    sizeLimit: documentSizeLimitFromEnv ?? 100 * bytesInMegabyte
  }
];

export const composerDocumentExtensionsAllowedAsFallback = [
  "dwg",
  "cdr",
  "psd",
  "json",
  "csv",
  "zip",
  "rar",
  "pfx",
  "dec",
  "rec"
];

export const composerFormatsAccepted =
  ".jpeg, .jpg, .png, .gif, .mp4, .pdf, .json, .csv, .zip, .rar, .pfx, .txt, .doc, .docx, .xls, .xlsx, .ogg, .amr, .webp, .3gp, .ppt, .pptx, .ods, .opus, .dwg, .cdr, .eps, .ai, .psd, .svg, .dec, .rec";

const getAttachmentId = () => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `attachment-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const getAllowedFileType = (file: File) => {
  let fileType = allowedComposerFileTypes.find((type) =>
    type.mimeTypes.includes(file.type)
  );

  if (!fileType) {
    const extension = file.name.split(".").pop()?.toLowerCase();

    if (
      extension &&
      composerDocumentExtensionsAllowedAsFallback.includes(extension)
    ) {
      fileType = allowedComposerFileTypes.find(
        (type) => type.fileType === "documents"
      );
    }
  }

  return fileType;
};

const getSizeLimitLabel = (fileType: AllowedComposerFileType) => {
  if (fileType.fileType === "sticker") return "500 KB";

  const sizeInMegabytes = fileType.sizeLimit / bytesInMegabyte;
  const formattedSize = Number.isInteger(sizeInMegabytes)
    ? String(sizeInMegabytes)
    : sizeInMegabytes.toFixed(2).replace(/\.?0+$/, "");

  return `${formattedSize} MB`;
};

const getExceededLimitToastMessage = (
  exceededFileTypes: AllowedComposerFileType[]
) => {
  if (!exceededFileTypes.length) {
    return "Um ou mais arquivos não foram anexados por excederem o tamanho limite.";
  }

  const mediaFileType = allowedComposerFileTypes.find(
    (fileType) => fileType.fileType === "video"
  );
  const documentFileType = allowedComposerFileTypes.find(
    (fileType) => fileType.fileType === "documents"
  );

  const mediaLimitLabel = mediaFileType
    ? getSizeLimitLabel(mediaFileType)
    : "16 MB";
  const documentLimitLabel = documentFileType
    ? getSizeLimitLabel(documentFileType)
    : "100 MB";

  return `Um ou mais arquivos não foram anexados por excederem o tamanho limite de ${mediaLimitLabel} para mídias e ${documentLimitLabel} para documentos.`;
};

const getKindLabel = (kind: ComposerAttachmentKind) => {
  if (kind === "image") return "Imagem";
  if (kind === "audio") return "Audio";
  if (kind === "video") return "Video";

  return "Arquivo";
};

export const getComposerAttachmentKind = (
  file: File
): ComposerAttachmentKind => {
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("audio/")) return "audio";
  if (file.type.startsWith("video/")) return "video";

  return "other";
};

export const validateComposerFiles = (
  files: File[]
): ComposerFilesValidationResult => {
  const acceptedFiles: File[] = [];
  const exceededFileTypes: AllowedComposerFileType[] = [];

  for (const file of files) {
    const fileType = getAllowedFileType(file);

    if (!fileType) {
      return {
        acceptedFiles: [],
        errorMessage: `Formato de arquivo invalido. Formatos permitidos: ${composerFormatsAccepted
          .split(",")
          .map((el) => el.trim().replace(".", ""))
          .join("/")}.`,
        partialErrorMessage: null
      };
    }

    console.log("[composer-attachments] file validation", {
      fileName: file.name,
      fileSizeMb: Number((file.size / bytesInMegabyte).toFixed(2)),
      allowedSizeMb: Number((fileType.sizeLimit / bytesInMegabyte).toFixed(2)),
      fileType: fileType.fileType,
      mediaSizeLimitEnv: process.env.OMNICHANNEL_MAX_MEDIA_UPLOAD_SIZE_MB,
      documentSizeLimitEnv: process.env.OMNICHANNEL_MAX_DOCUMENT_UPLOAD_SIZE_MB
    });

    if (file.size > fileType.sizeLimit) {
      if (files.length === 1) {
        return {
          acceptedFiles: [],
          errorMessage: `Tamanho máximo excedido. Máximo permitido para este tipo de arquivo: ${getSizeLimitLabel(
            fileType
          )}`,
          partialErrorMessage: null
        };
      }

      exceededFileTypes.push(fileType);
      continue;
    }

    acceptedFiles.push(file);
  }

  return {
    acceptedFiles,
    errorMessage: null,
    partialErrorMessage: exceededFileTypes.length
      ? getExceededLimitToastMessage(exceededFileTypes)
      : null
  };
};

export const buildComposerAttachments = (files: File[]) =>
  files.map((file) => ({
    id: getAttachmentId(),
    file,
    previewUrl: URL.createObjectURL(file),
    kind: getComposerAttachmentKind(file)
  }));

export const revokeComposerAttachments = (
  attachments: ComposerAttachment[]
) => {
  attachments.forEach((attachment) =>
    URL.revokeObjectURL(attachment.previewUrl)
  );
};

export const createFileListFromFiles = (files: File[]) => {
  const dataTransfer = new DataTransfer();

  files.forEach((file) => dataTransfer.items.add(file));

  return dataTransfer.files;
};

interface ComposerAttachmentsPreviewProps {
  attachments: ComposerAttachment[];
  mainColor?: string;
  onRemove: (id: string) => void;
  disabled?: boolean;
  uploadProgress?: AttachmentUploadProgress | null;
}

export const ComposerAttachmentsPreview = ({
  attachments,
  mainColor,
  onRemove,
  disabled = false,
  uploadProgress
}: ComposerAttachmentsPreviewProps) => {
  if (!attachments.length) return null;

  return (
    <ComposerAttachmentsList>
      {attachments.map((attachment, index) => (
        <ComposerAttachmentCard key={attachment.id}>
          <ComposerAttachmentPreview>
            {attachment.kind === "image" ? (
              <img src={attachment.previewUrl} alt={attachment.file.name} />
            ) : null}

            {attachment.kind === "video" ? (
              <video src={attachment.previewUrl} muted />
            ) : null}

            {attachment.kind === "audio" ? (
              <ComposerAttachmentFallback>Audio</ComposerAttachmentFallback>
            ) : null}

            {attachment.kind === "other" ? (
              <ComposerAttachmentFallback>Arquivo</ComposerAttachmentFallback>
            ) : null}

            {uploadProgress?.index === index ? (
              <ComposerAttachmentUploadOverlay>
                <UploadProgressSpinner progress={uploadProgress.progress} />
              </ComposerAttachmentUploadOverlay>
            ) : null}
          </ComposerAttachmentPreview>

          <ComposerAttachmentInfo>
            <ComposerAttachmentKindLabel $mainColor={mainColor}>
              {getKindLabel(attachment.kind)}
            </ComposerAttachmentKindLabel>

            {attachment.file.name ? (
              <ComposerAttachmentName>
                {attachment.file.name}
              </ComposerAttachmentName>
            ) : null}
          </ComposerAttachmentInfo>

          <ComposerAttachmentRemoveButton
            $mainColor={mainColor}
            type="button"
            onClick={() => {
              if (disabled) return;
              onRemove(attachment.id);
            }}
            aria-label={`Remover ${attachment.file.name}`}
            disabled={disabled}
          >
            <i className="pi pi-times" />
          </ComposerAttachmentRemoveButton>
        </ComposerAttachmentCard>
      ))}
    </ComposerAttachmentsList>
  );
};
